import { useState } from 'react';
import { activityRegistrationService } from '../apiService';
import { prepareParticipantDataForAPI } from '../utils';

/**
 * Hook personnalisé pour gérer la soumission des participants
 */
export const useSubmission = () => {
  // États pour la soumission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResults, setSubmissionResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  /**
   * Soumet tous les participants vers l'API
   * @param {Array} participants - Liste des participants
   * @param {string} selectedActivity - ID de l'activité sélectionnée
   * @param {Function} onError - Callback en cas d'erreur générale
   */
  const submitAllParticipants = async (participants, selectedActivity, onError) => {
    if (participants.length === 0) {
      alert('Aucun participant à inscrire');
      return;
    }

    setIsSubmitting(true);

    try {
      const results = await activityRegistrationService.submitAllParticipants(
        participants,
        selectedActivity,
        prepareParticipantDataForAPI
      );
      
      setSubmissionResults(results);
      setShowResults(true);
      
    } catch (error) {
      console.error('Erreur générale lors de la soumission:', error);
      if (onError) {
        onError('Erreur lors de l\'inscription des participants');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Recommence une nouvelle inscription
   * @param {Function} clearParticipants - Fonction pour vider la liste des participants
   * @param {Function} resetActivity - Fonction pour réinitialiser l'activité sélectionnée
   */
  const startNewInscription = (clearParticipants, resetActivity) => {
    setShowResults(false);
    setSubmissionResults(null);
    if (clearParticipants) clearParticipants();
    if (resetActivity) resetActivity();
  };

  /**
   * Calcule les statistiques des résultats
   * @returns {Object} Statistiques des soumissions
   */
  const getSubmissionStats = () => {
    if (!submissionResults) {
      return { successCount: 0, errorCount: 0, total: 0 };
    }

    const successCount = submissionResults.filter(r => r.success).length;
    const errorCount = submissionResults.filter(r => !r.success).length;
    const total = submissionResults.length;

    return { successCount, errorCount, total };
  };

  /**
   * Détermine le type de résultat global
   * @returns {string} Type de résultat: 'success', 'error', ou 'partial'
   */
  const getResultType = () => {
    const { successCount, errorCount, total } = getSubmissionStats();
    
    if (successCount === total) return 'success';
    if (errorCount === total) return 'error';
    return 'partial';
  };

  /**
   * Obtient le message de titre approprié
   * @returns {string} Message de titre
   */
  const getTitleMessage = () => {
    const resultType = getResultType();
    
    switch (resultType) {
      case 'success':
        return '✅ Inscription réussie !';
      case 'error':
        return '❌ Erreur d\'inscription';
      case 'partial':
        return '⚠️ Inscription partiellement réussie';
      default:
        return '';
    }
  };

  /**
   * Obtient la couleur appropriée pour le titre
   * @returns {string} Couleur CSS
   */
  const getTitleColor = () => {
    const resultType = getResultType();
    
    switch (resultType) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#d32f2f';
      case 'partial':
        return '#ff9800';
      default:
        return '#000000';
    }
  };

  return {
    // États
    isSubmitting,
    submissionResults,
    showResults,

    // Actions
    submitAllParticipants,
    startNewInscription,

    // Utilitaires
    getSubmissionStats,
    getResultType,
    getTitleMessage,
    getTitleColor
  };
};